import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CLIENTES_KEYS,
  CLIENT_CLASSIFICATIONS_KEYS,
  CONTACT_RESPONSIBILITIES_KEYS,
  CONTATOS_KEYS,
  ChangeResponsiblePayload,
  CreateClientePayload,
  CreateContatoPayload,
  CreateEnderecoPayload,
  DOCUMENTOS_KEYS,
  ENDERECOS_KEYS,
  HISTORICO_KEYS,
  ListClientesParams,
  ListHistoryParams,
  ListTimelineParams,
  PENDING_CHANGES_KEYS,
  PendingChangeStatus,
  RESPONSAVEL_KEYS,
  TIMELINE_KEYS,
  TransicaoCliente,
  VALIDATION_KEYS,
  aprovarPendingChange,
  buscarValidation,
  listarPendingChanges,
  rejeitarPendingChange,
  UpdateClientePayload,
  UpdateContatoPayload,
  UpdateEnderecoPayload,
  UpsertClassificationPayload,
  atualizarClassificacao,
  atualizarCliente,
  atualizarClienteDocumento,
  atualizarContato,
  atualizarEndereco,
  buscarCliente,
  buscarElegibilidade,
  buscarSummary,
  criarClassificacao,
  criarCliente,
  criarContato,
  criarEndereco,
  definirContatoPrincipal,
  definirEnderecoPrincipal,
  excluirCliente,
  excluirContato,
  excluirDocumento,
  excluirEndereco,
  listarClassificacoes,
  listarClientes,
  listarContatos,
  listarResponsabilidadesContato,
  listarAfastamentos,
  afastarContato,
  encerrarAfastamento,
  substituirContato,
  buscarContatoSugerido,
  listarDocumentos,
  listarEnderecos,
  listarHistorico,
  listarHistoricoResponsavel,
  listarTimeline,
  trocarResponsavel,
  transicionarCliente,
  uploadDocumento,
} from '../api';
import type { ContactIntent } from '../api';

export function useClientes(params?: ListClientesParams) {
  return useQuery({
    queryKey: CLIENTES_KEYS.list(params),
    queryFn: () => listarClientes(params),
  });
}

export function useCliente(id: string | undefined) {
  return useQuery({
    queryKey: id ? CLIENTES_KEYS.detail(id) : ['clientes', 'detail', 'noop'],
    queryFn: () => buscarCliente(id as string),
    enabled: !!id,
  });
}

export function useCriarCliente() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateClientePayload) => criarCliente(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: CLIENTES_KEYS.all }),
  });
}

export function useAtualizarCliente() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateClientePayload }) =>
      atualizarCliente(id, data),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: CLIENTES_KEYS.all });
      qc.invalidateQueries({ queryKey: CLIENTES_KEYS.detail(vars.id) });
      qc.invalidateQueries({ queryKey: VALIDATION_KEYS.base(vars.id) });
    },
  });
}

export function useAtualizarClienteDocumento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, documento }: { id: string; documento: string }) =>
      atualizarClienteDocumento(id, documento),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: CLIENTES_KEYS.all });
      qc.invalidateQueries({ queryKey: CLIENTES_KEYS.detail(vars.id) });
      qc.invalidateQueries({ queryKey: VALIDATION_KEYS.base(vars.id) });
    },
  });
}

// ============================================================
// Fase 2 — Transições, DELETE, elegibilidade, endereços
// ============================================================

export function useTransicionarCliente() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, reason }: { id: string; action: TransicaoCliente; reason?: string }) =>
      transicionarCliente(id, action, reason),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: CLIENTES_KEYS.all });
      qc.invalidateQueries({ queryKey: CLIENTES_KEYS.detail(vars.id) });
      qc.invalidateQueries({ queryKey: VALIDATION_KEYS.base(vars.id) });
    },
  });
}

export function useExcluirCliente() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => excluirCliente(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: CLIENTES_KEYS.all }),
  });
}

export function useElegibilidadeCliente(id: string | undefined) {
  return useQuery({
    queryKey: id ? ['clientes', id, 'eligibility'] : ['clientes', 'eligibility', 'noop'],
    queryFn: () => buscarElegibilidade(id as string),
    enabled: !!id,
  });
}

export function useEnderecos(clientId: string | undefined) {
  return useQuery({
    queryKey: clientId ? ENDERECOS_KEYS.all(clientId) : ['clientes', 'noop', 'enderecos'],
    queryFn: () => listarEnderecos(clientId as string),
    enabled: !!clientId,
  });
}

export function useCriarEndereco(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateEnderecoPayload) => criarEndereco(clientId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ENDERECOS_KEYS.all(clientId) });
      qc.invalidateQueries({ queryKey: VALIDATION_KEYS.base(clientId) });
    },
  });
}

export function useAtualizarEndereco(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ addressId, data }: { addressId: string; data: UpdateEnderecoPayload }) =>
      atualizarEndereco(clientId, addressId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ENDERECOS_KEYS.all(clientId) });
      qc.invalidateQueries({ queryKey: VALIDATION_KEYS.base(clientId) });
    },
  });
}

export function useDefinirEnderecoPrincipal(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (addressId: string) => definirEnderecoPrincipal(clientId, addressId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ENDERECOS_KEYS.all(clientId) });
      qc.invalidateQueries({ queryKey: VALIDATION_KEYS.base(clientId) });
    },
  });
}

export function useExcluirEndereco(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (addressId: string) => excluirEndereco(clientId, addressId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ENDERECOS_KEYS.all(clientId) });
      qc.invalidateQueries({ queryKey: VALIDATION_KEYS.base(clientId) });
    },
  });
}

// ============================================================
// Fase 3 — Histórico
// ============================================================

export function useHistoricoCliente(clientId: string | undefined, params?: ListHistoryParams) {
  return useQuery({
    queryKey: clientId
      ? HISTORICO_KEYS.list(clientId, params)
      : ['clientes', 'historico', 'noop'],
    queryFn: () => listarHistorico(clientId as string, params),
    enabled: !!clientId,
  });
}

// ============================================================
// Fase 4 — Summary + Documentos
// ============================================================

export function useSummaryCliente(clientId: string | undefined) {
  return useQuery({
    queryKey: clientId ? ['clientes', clientId, 'summary'] : ['clientes', 'summary', 'noop'],
    queryFn: () => buscarSummary(clientId as string),
    enabled: !!clientId,
  });
}

export function useDocumentosCliente(clientId: string | undefined) {
  return useQuery({
    queryKey: clientId ? DOCUMENTOS_KEYS.all(clientId) : ['clientes', 'noop', 'documentos'],
    queryFn: () => listarDocumentos(clientId as string),
    enabled: !!clientId,
  });
}

export function useUploadDocumento(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { tipo: string; nome: string; expiresAt?: string; file: File }) =>
      uploadDocumento(clientId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DOCUMENTOS_KEYS.all(clientId) });
      qc.invalidateQueries({ queryKey: ['clientes', clientId, 'summary'] });
      qc.invalidateQueries({ queryKey: HISTORICO_KEYS.all(clientId) });
    },
  });
}

export function useExcluirDocumento(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => excluirDocumento(clientId, documentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DOCUMENTOS_KEYS.all(clientId) });
      qc.invalidateQueries({ queryKey: ['clientes', clientId, 'summary'] });
      qc.invalidateQueries({ queryKey: HISTORICO_KEYS.all(clientId) });
    },
  });
}

// ============================================================
// SPEC 02 §1 — Contatos e Responsável Comercial (Fase A)
// ============================================================

export function useContatos(clientId: string | undefined) {
  return useQuery({
    queryKey: clientId ? CONTATOS_KEYS.all(clientId) : ['clientes', 'noop', 'contatos'],
    queryFn: () => listarContatos(clientId as string),
    enabled: !!clientId,
  });
}

// Helper: invalida TODAS as queries derivadas de contatos do cliente.
// Inclui `contato-sugerido` (usado em Faturamento/OS/Propostas) — a key
// dessa query NÃO compartilha prefixo com `CONTATOS_KEYS.all`, então
// precisa ser invalidada explicitamente.
function invalidateContactRelated(qc: ReturnType<typeof useQueryClient>, clientId: string) {
  qc.invalidateQueries({ queryKey: CONTATOS_KEYS.all(clientId) });
  qc.invalidateQueries({ queryKey: ['clientes', clientId, 'contato-sugerido'] });
  qc.invalidateQueries({ queryKey: HISTORICO_KEYS.all(clientId) });
  qc.invalidateQueries({ queryKey: VALIDATION_KEYS.base(clientId) });
}

export function useCriarContato(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateContatoPayload) => criarContato(clientId, payload),
    onSuccess: () => {
      invalidateContactRelated(qc, clientId);
      qc.invalidateQueries({ queryKey: ['clientes', clientId, 'summary'] });
    },
  });
}

export function useAtualizarContato(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, data }: { contactId: string; data: UpdateContatoPayload }) =>
      atualizarContato(clientId, contactId, data),
    onSuccess: () => {
      invalidateContactRelated(qc, clientId);
    },
  });
}

export function useDefinirContatoPrincipal(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contactId: string) => definirContatoPrincipal(clientId, contactId),
    onSuccess: () => {
      invalidateContactRelated(qc, clientId);
    },
  });
}

export function useExcluirContato(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contactId: string) => excluirContato(clientId, contactId),
    onSuccess: () => {
      invalidateContactRelated(qc, clientId);
      qc.invalidateQueries({ queryKey: ['clientes', clientId, 'summary'] });
    },
  });
}

export function useHistoricoResponsavel(clientId: string | undefined) {
  return useQuery({
    queryKey: clientId ? RESPONSAVEL_KEYS.history(clientId) : ['clientes', 'noop', 'responsavel'],
    queryFn: () => listarHistoricoResponsavel(clientId as string),
    enabled: !!clientId,
  });
}

export function useTrocarResponsavel(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ChangeResponsiblePayload) => trocarResponsavel(clientId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: RESPONSAVEL_KEYS.history(clientId) });
      qc.invalidateQueries({ queryKey: CLIENTES_KEYS.detail(clientId) });
      qc.invalidateQueries({ queryKey: HISTORICO_KEYS.all(clientId) });
      qc.invalidateQueries({ queryKey: VALIDATION_KEYS.base(clientId) });
    },
  });
}

// ============================================================
// SPEC 02 §1 — Classificação parametrizável + Timeline (Fase B)
// ============================================================

export function useClassificacoes(includeInactive = false) {
  return useQuery({
    queryKey: CLIENT_CLASSIFICATIONS_KEYS.all(includeInactive),
    queryFn: () => listarClassificacoes(includeInactive),
    // Tabela de domínio — cacheia por mais tempo; muda raramente.
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * SPEC 03 — responsabilidades de contato (Gestor do Contrato,
 * Aprovador de Medição, etc.). Tabela de domínio administrável.
 */
export function useResponsabilidadesContato(includeInactive = false) {
  return useQuery({
    queryKey: CONTACT_RESPONSIBILITIES_KEYS.all(includeInactive),
    queryFn: () => listarResponsabilidadesContato(includeInactive),
    staleTime: 5 * 60 * 1000,
  });
}

// SPEC 03 Fase B — substituição definitiva
export function useSubstituirContato(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      contactId,
      newContactId,
      reason,
    }: {
      contactId: string;
      newContactId: string;
      reason: string;
    }) => substituirContato(clientId, contactId, { newContactId, reason }),
    onSuccess: () => {
      invalidateContactRelated(qc, clientId);
    },
  });
}

// SPEC 03 Fase B — afastamento temporário
export function useAfastamentosContato(clientId: string, contactId: string | undefined) {
  return useQuery({
    queryKey: contactId
      ? (['clientes', clientId, 'contatos', contactId, 'afastamentos'] as const)
      : (['clientes', 'noop', 'afastamentos'] as const),
    queryFn: () => listarAfastamentos(clientId, contactId as string),
    enabled: !!contactId,
  });
}

export function useAfastarContato(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      contactId,
      substituteContactId,
      startsAt,
      endsAt,
      reason,
    }: {
      contactId: string;
      substituteContactId: string;
      startsAt: string;
      endsAt: string;
      reason?: string;
    }) =>
      afastarContato(clientId, contactId, {
        substituteContactId,
        startsAt,
        endsAt,
        reason,
      }),
    onSuccess: (_res, vars) => {
      invalidateContactRelated(qc, clientId);
      qc.invalidateQueries({
        queryKey: ['clientes', clientId, 'contatos', vars.contactId, 'afastamentos'],
      });
    },
  });
}

export function useEncerrarAfastamento(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, absenceId }: { contactId: string; absenceId: string }) =>
      encerrarAfastamento(clientId, contactId, absenceId),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({
        queryKey: ['clientes', clientId, 'contatos', vars.contactId, 'afastamentos'],
      });
      invalidateContactRelated(qc, clientId);
    },
  });
}

export function useCriarClassificacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpsertClassificationPayload) => criarClassificacao(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client-classifications'] });
    },
  });
}

export function useAtualizarClassificacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpsertClassificationPayload }) =>
      atualizarClassificacao(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client-classifications'] });
    },
  });
}

export function useTimeline(clientId: string | undefined, params?: ListTimelineParams) {
  return useQuery({
    queryKey: clientId ? TIMELINE_KEYS.list(clientId, params) : ['clientes', 'noop', 'timeline'],
    queryFn: () => listarTimeline(clientId as string, params),
    enabled: !!clientId,
    // Timeline muda com cada evento; margens curtas de cache evitam UI stale.
    staleTime: 30 * 1000,
  });
}

// ============================================================
// SPEC 02 §1 — Validação, completude e rascunho (Fase C)
// ============================================================

export function useValidation(clientId: string | undefined) {
  return useQuery({
    queryKey: clientId ? VALIDATION_KEYS.base(clientId) : ['clientes', 'noop', 'validation'],
    queryFn: () => buscarValidation(clientId as string),
    enabled: !!clientId,
    // Mesma janela do cache do backend (30s) — mantém coerência entre
    // o service e o frontend; mutação em qualquer entidade filha
    // invalida a query.
    staleTime: 25 * 1000,
  });
}

// ============================================================
// SPEC 02 §1 — Aprovações pendentes (Fase D)
// ============================================================

export function usePendingChanges(params?: { clientId?: string; status?: PendingChangeStatus }) {
  return useQuery({
    queryKey: PENDING_CHANGES_KEYS.list(params),
    queryFn: () => listarPendingChanges(params),
    staleTime: 15 * 1000,
  });
}

export function useAprovarPendingChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      aprovarPendingChange(id, reason),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: PENDING_CHANGES_KEYS.base });
      // Recarrega o cliente porque o campo foi de fato alterado.
      qc.invalidateQueries({ queryKey: CLIENTES_KEYS.detail(res.clientId) });
      qc.invalidateQueries({ queryKey: HISTORICO_KEYS.all(res.clientId) });
      qc.invalidateQueries({ queryKey: VALIDATION_KEYS.base(res.clientId) });
    },
  });
}

export function useRejeitarPendingChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      rejeitarPendingChange(id, reason),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: PENDING_CHANGES_KEYS.base });
      qc.invalidateQueries({ queryKey: HISTORICO_KEYS.all(res.clientId) });
    },
  });
}

/**
 * SPEC 03 Fase C — sugestão automática de contato por intent.
 * Consumido pela aba Faturamento (Responsável Financeiro sugerido) e,
 * futuramente, pelos módulos de OS/medições/propostas.
 */
export function useContatoSugerido(
  clientId: string | undefined,
  intent: ContactIntent,
) {
  return useQuery({
    queryKey: clientId
      ? (['clientes', clientId, 'contato-sugerido', intent] as const)
      : (['clientes', 'noop', 'contato-sugerido'] as const),
    queryFn: () => buscarContatoSugerido(clientId as string, intent),
    enabled: !!clientId,
    // Invalidação explícita via `invalidateContactRelated` nas 7 mutations
    // de contato/afastamento (a key `contato-sugerido` NÃO compartilha
    // prefixo com CONTATOS_KEYS, então precisa ser invalidada à parte).
    staleTime: 60 * 1000,
  });
}
